import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {safeLocalStorage} from "../../util/safeStorage";
import {InfiniteData, QueryStatus, useInfiniteQuery, useMutation} from '@tanstack/react-query';
import {FixedSizeList} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import {Button, message, Modal, Radio, Typography, notification} from 'antd';
import {CheckCircleOutlined} from '@ant-design/icons';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import styled from 'styled-components';
import {IJobDemand, IJobDetails, ILocation, IPosition} from "../../services/types.ts";
import {alertOptions, defaultWhereOptions} from "../../util/utils.ts";
import {AuthContext} from "../../context/AuthContext.tsx";
import {addSearch, getJobPool, updateSearchById} from "../../services/api.ts";
import {JobSearchFilters} from "./new/JobSearchFilters.tsx";
import {JobCountDisplay} from "./new/JobCountDisplay.tsx";
import {VirtualizedJobsList} from "./new/VirtualizedJobsList.tsx";
import {useQueryState} from 'nuqs';
import {useJobPoolCount} from "./hooks/useJobPool.ts";
import {ProfessionFilterModal} from "client/src/pages/candidatePool/components/ProfessionFilterModal.tsx";
import {LocationFilterModal} from "client/src/pages/candidatePool/components/LocationFilterModal.tsx";
import {useSubscriptionEndDate} from "../../hooks/useSubscriptionEndDate.ts";
import {PromoAlert} from "../../components/PromoAlert.tsx";

const {Title} = Typography;

// --- Constants ---
const LIMIT = 20;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 8px);
    box-sizing: border-box;
`;

export const JobPoolNew: React.FC = () => {
    const [selectedPosition, setSelectedPosition] = useQueryState<IPosition | null>(
        'position',
        {
            parse: (value: string) => value ? JSON.parse(value) as IPosition : null,
            serialize: (value: IPosition | null) => value ? JSON.stringify(value) : '',
            defaultValue: null
        }
    );
    const [selectedLocation, setSelectedLocation] = useQueryState<ILocation | null>(
        'location',
        {
            parse: (value: string) => value ? JSON.parse(value) as ILocation : null,
            serialize: (value: ILocation | null) => value ? JSON.stringify(value) : '',
            defaultValue: defaultWhereOptions[0]
        }
    );
    const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [selectedAlertOption, setSelectedAlertOption] = useState(alertOptions[0]);
    const [savedSearchItem, setSavedSearchItem] = useState<IJobDemand | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);

    const listRef = useRef<FixedSizeList>(null);
    const infiniteLoaderRef = useRef<InfiniteLoader>(null);

    const navigate = useNavigate();
    const {user} = useContext(AuthContext);
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const paymentStatus = searchParams.get('paymentStatus');

        if (paymentStatus === 'success') {
            notification.success({
                message: 'silver+ Vorteile erfolgreich freigeschaltet',
                placement: 'topRight'
            });
            // Remove the param from URL
            searchParams.delete('paymentStatus');
            setSearchParams(searchParams);
        } else if (paymentStatus === 'failed') {
            notification.error({
                message: 'Da ist etwas schiefgelaufen',
                placement: 'topRight'
            });
            // Remove the param from URL
            searchParams.delete('paymentStatus');
            setSearchParams(searchParams);
        }
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        // Only handle magic link redirects here, not normal authentication redirects
        const magicLinkRedirect = safeLocalStorage.getItem('appRedirectPath');

        if (magicLinkRedirect && magicLinkRedirect !== "/" && magicLinkRedirect !== location.pathname) {
            // Verify this is actually a magic link by checking URL parameters
            const currentUrl = new URL(window.location.href);
            const hasParams = currentUrl.searchParams.has('param2') || currentUrl.searchParams.has('id') || currentUrl.searchParams.has('target');

            if (hasParams) {
                const redirectTo = magicLinkRedirect;
                safeLocalStorage.removeItem("appRedirectPath");
                navigate(redirectTo);
            } else {
                // Clean up stale magic link redirects
                safeLocalStorage.removeItem("appRedirectPath");
            }
        }
    }, [navigate])

    // --- Data Fetching ---
    const {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        status,
    } = useInfiniteQuery<
        IJobDetails[],
        Error,
        InfiniteData<IJobDetails[], number>,
        [string, number | string | undefined, number | string | undefined],
        number
    >({
        queryKey: ['jobPool', selectedPosition?.id ?? "all", selectedLocation?.id ?? "all"],
        queryFn: ({ pageParam }) => getJobPool({
            limit: LIMIT,
            offset: pageParam as number,
            positionId: selectedPosition?.id,
            locationId: selectedLocation?.id
        }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            const lastPageLength = lastPage.filter((job: IJobDetails) => job?.id !== -1).length;

            // If we didn't get a full page, there are no more pages
            if (lastPageLength < LIMIT) {
                return undefined;
            }

            // Calculate the next offset
            return allPages.flat().length;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        enabled: !!selectedPosition?.id || selectedPosition === null,
    });

    const {
        data: numberOfJobs,
        isLoading: isLoadingCount
    } = useJobPoolCount(selectedPosition || undefined, selectedLocation || undefined);

    // Check subscription status
    const {endDate} = useSubscriptionEndDate();
    const hasActiveSubscription = endDate && !isNaN(endDate.getTime()) && endDate > new Date();

    // --- Mutations ---
    const {mutate: saveSearchMutation, isPending: isSavingSearch} = useMutation({
        mutationFn: (jobDemand: Partial<IJobDemand>) => addSearch(jobDemand),
        onSuccess: (response: IJobDetails) => {
            setSavedSearchItem(response as unknown as IJobDemand);
            setIsAlertModalOpen(true);
            message.success('Suche gespeichert. Richten Sie nun Benachrichtigungen ein.');
        },
        onError: (error: Error | { message?: string }) => {
            console.error('Error saving filter:', error);
            message.error(`Speichern fehlgeschlagen: ${error?.message || 'Bitte versuchen Sie es erneut.'}`);
        },
    });

    const updateMutation = useMutation({
        mutationFn: (updatedData: Partial<IJobDetails>) =>
            updateSearchById(savedSearchItem?.id ?? 0, updatedData),
        onSuccess: () => {
            message.success('Eingabe erfolgreich gespeichert');
            setIsAlertModalOpen(false);
        },
        onError: (error) => {
            message.error(`Da ist etwas schiefgelaufen: ${error}`);
        },
    });

    // --- Memos and Callbacks ---
    const jobs = useMemo(() => {
        // Flatten pages and filter out jobs with id === 0
        const realJobs = data?.pages ? data.pages.flatMap(page => page.filter(job => job.id !== 0)) : [];

        // Only add promotional item if user doesn't have active subscription
        if (!hasActiveSubscription) {
            const promotionalItem: IJobDetails = {
                id: -1,
                isPromotional: true,
            } as IJobDetails;

            if (!data?.pages) {
                // No data yet, return only promotional item
                return [promotionalItem];
            }

            // Append promotional item at the end
            return [...realJobs, promotionalItem];
        }

        return realJobs;
    }, [data, hasActiveSubscription]);

    // Reset infinite loader cache and scroll to top when filters change
    useEffect(() => {
        infiniteLoaderRef.current?.resetloadMoreItemsCache();
        listRef.current?.scrollToItem(0);
    }, [selectedPosition, selectedLocation]);

    const loadMoreItems = useCallback(() => {
        // Only fetch next page if not already fetching and there is a next page
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    // isItemLoaded tells InfiniteLoader if an item at a specific index is loaded
    const isItemLoaded = useCallback((index: number) => !hasNextPage || index < jobs.length, [hasNextPage, jobs.length]);

    // Calculate total item count for the list (candidates + 1 for loading indicator if hasNextPage)
    const itemCount = hasNextPage ? jobs.length + 1 : jobs.length;

    // --- Event Handlers ---
    const handleSavePosition = useCallback((position: IPosition | null) => {
        setSelectedPosition(position);
        setIsPositionModalOpen(false); // Close modal on save
    }, [setSelectedPosition]);

    const handleSaveLocation = useCallback((location: ILocation | null) => {
        setSelectedLocation(location);
        setIsLocationModalOpen(false); // Close modal on save
    }, [setSelectedLocation]);

    const handleClearPosition = useCallback(() => {
        setSelectedPosition(null);
    }, [setSelectedPosition]);

    const handleClearLocation = useCallback(() => {
        setSelectedLocation(null);
    }, [setSelectedLocation]);

    const handleSaveFilterClick = useCallback(() => {
        if (!selectedPosition?.id) {
            message.warning('Bitte wählen Sie zuerst aus, was Sie suchen (Beruf/Tätigkeit).');
            return;
        }
        if (!user?.id || user.candidate_id === undefined || user.candidate_id === null) {
            message.error('Fehler: Benutzerdaten unvollständig. Bitte neu anmelden.');
            return;
        }

        const searchParam: Partial<IJobDemand> = {
            candidate_id: user!.candidate_id,
            users_id: user!.id,
            alert_config_id: 1,
            search_what_id: selectedPosition.id,
            search_location_id: selectedLocation?.id || null,
        };


        saveSearchMutation(searchParam as IJobDemand);

    }, [selectedPosition?.id, user, selectedLocation?.id, saveSearchMutation]);

    const handleSaveAlertSettings = () => {
        if (savedSearchItem && selectedPosition) {
            const updatePayload: Partial<IJobDetails> = {
                alert_label: selectedAlertOption.label,
                alert_config_id: selectedAlertOption.id,
            };
            updateMutation.mutate(updatePayload as Partial<IJobDetails>);
        }
    };

    return (
        <Container>
            {!hasActiveSubscription && <PromoAlert/>}
            <JobSearchFilters
                selectedPosition={selectedPosition}
                selectedLocation={selectedLocation}
                onPositionClick={() => setIsPositionModalOpen(true)}
                onLocationClick={() => setIsLocationModalOpen(true)}
                onClearPosition={handleClearPosition}
                onClearLocation={handleClearLocation}
                onSaveSearchClick={handleSaveFilterClick}
                isSaveSearchDisabled={!selectedPosition?.id}
                isSavingSearch={isSavingSearch}
            />

            <JobCountDisplay
                count={numberOfJobs?.id}
                isLoading={isLoadingCount}
            />

            <VirtualizedJobsList
                jobs={jobs}
                status={status as QueryStatus}
                error={error}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={hasNextPage}
                itemCount={itemCount}
                loadMoreItems={loadMoreItems}
                isItemLoaded={isItemLoaded}
                listRef={listRef}
                infiniteLoaderRef={infiniteLoaderRef}
            />

            <ProfessionFilterModal
                isOpen={isPositionModalOpen}
                onClose={() => setIsPositionModalOpen(false)}
                onSave={handleSavePosition}
                initialSelectedPosition={selectedPosition}
            />
            <LocationFilterModal
                isCandidate={true}
                isOpen={isLocationModalOpen}
                onClose={() => setIsLocationModalOpen(false)}
                onSave={handleSaveLocation}
                initialSelectedLocation={selectedLocation}
            />

            <Modal
                title="Ihre Jobsuche wurde gespeichert"
                open={isAlertModalOpen}
                onCancel={() => setIsAlertModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsAlertModalOpen(false)}
                            onTouchStart={() => setIsAlertModalOpen(false)}>
                        Abbrechen
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleSaveAlertSettings}>
                        Speichern
                    </Button>,
                ]}
            >
                <p><CheckCircleOutlined style={{color: 'green'}}/> Eine Übersicht finden Sie unter: <Link
                    to="/searches">Mein Konto {">"} Jobsuchen</Link></p>

                <Title level={5}>Benachrichtigung per Email</Title>
                <p>Bestimmen Sie wie häufig wir Sie über passende neue Kandidaten informieren</p>
                <Radio.Group
                    style={{display: "flex", flexDirection: 'column'}}
                    onChange={(e) => setSelectedAlertOption(alertOptions.find(option => option.id === e.target.value)!)}
                    value={selectedAlertOption.id}
                >
                    {alertOptions.map(option => (
                        <Radio key={option.id} value={option.id}>{option.label}</Radio>
                    ))}
                </Radio.Group>
            </Modal>
        </Container>
    );
};
